/** The signed-in user, resolved once at startup.
 *
 *  Three outcomes, and the app has a screen for each: signed in with a role,
 *  not signed in, and signed in but not on the access list. That last one is
 *  why this returns a discriminated state rather than `user | null` — "who are
 *  you" and "are you allowed" fail differently and need different words on
 *  screen.
 *
 *  Nothing here decides anything. The role it reports is used to hide controls
 *  the caller may not use; the server refuses them regardless, and is the only
 *  thing that actually enforces the rules.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { ApiError, api, type AccessDenied, type SessionUser } from "./api";

export type Session =
  | { status: "loading" }
  | { status: "in"; user: SessionUser }
  | { status: "out" }
  | { status: "denied"; denial: AccessDenied }
  | { status: "error"; message: string };

const Ctx = createContext<Session>({ status: "loading" });

export function useSession(): Session {
  return useContext(Ctx);
}

/** The user, for the many call sites that run inside an authenticated tree and
 *  only want the role. */
export function useUser(): SessionUser | null {
  const session = useSession();
  return session.status === "in" ? session.user : null;
}

/** What the role allows, named once so no view has to remember the mapping.
 *  Mirrors CAN_WRITE / CAN_DELETE / CAN_GRANT in `app/store/users.py`. */
export function usePermissions() {
  const role = useUser()?.role;
  return {
    role,
    canWrite: role === "admin" || role === "contributor",
    canDelete: role === "admin",
    isAdmin: role === "admin",
  };
}

function isDenial(detail: unknown): detail is AccessDenied {
  return typeof detail === "object" && detail !== null && "access" in detail;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session>({ status: "loading" });

  useEffect(() => {
    let live = true;
    api
      .me()
      .then((user) => live && setSession({ status: "in", user }))
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof ApiError && e.status === 401) return setSession({ status: "out" });
        if (e instanceof ApiError && e.status === 403 && isDenial(e.detail)) {
          return setSession({ status: "denied", denial: e.detail });
        }
        setSession({ status: "error", message: e instanceof Error ? e.message : String(e) });
      });
    return () => {
      live = false;
    };
  }, []);

  return <Ctx.Provider value={session}>{children}</Ctx.Provider>;
}

/** Hand the current URL to the server so the round trip through Google lands
 *  back where it started. Deep links are the navigation model here — every
 *  object has a hash URL somebody pastes into Slack. */
export function signInHref(): string {
  const here = location.pathname + location.search + location.hash;
  return `/api/auth/login?next=${encodeURIComponent(here)}`;
}

export function useSignOut() {
  return useCallback(async () => {
    try {
      await api.signOut();
    } finally {
      // A full reload rather than state juggling: everything on screen was
      // fetched as the old identity.
      location.href = "/";
    }
  }, []);
}
